import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import { Channel } from 'models/Channels';
import {
  AirbnbFeeType,
  AirbnbTaxType,
  BookingDotComFeeType,
  BookingDotComFeeTypeDeprecated,
  CleaningFeeCollection,
  FeeConditionEnum,
  FeeScopeEnum,
  FeeTypeEnum,
  HVMIFeeType,
  VrboFeeType,
} from 'models/PropertyFees';
import { useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'utils/arrayUtils';
import useFormData from './useFormData';
import {
  AIRBNB_PERCENT_FEE_TYPES,
  AIRBNB_UNIT_TYPE_OPTIONS_MAPPING,
  BY_LENGTH_OF_STAY_SCOPE_OPTION,
} from './PropertyFeeModal.constants';
import { PropertyFeeFormSchemaType } from './PropertyFeeModal.schema';

const BOOKING_OPTIONS_ORDER = [
  BookingDotComFeeType.RESORT_FEE,
  BookingDotComFeeType.SERVICE_CHARGE_FEE,
  BookingDotComFeeType.DESTINATION_FEE,
  BookingDotComFeeType.ENVIRONMENT_FEE,
  BookingDotComFeeType.MUNICIPALITY_FEE,
  BookingDotComFeeType.TRANSIT_FEE,
  BookingDotComFeeType.HERITAGE_FEE,
  BookingDotComFeeType.TOWEL_FEE,
  BookingDotComFeeType.ELECTRICITY_FEE,
  BookingDotComFeeType.LINEN_FEE,
  BookingDotComFeeType.GAS_FEE,
  BookingDotComFeeType.OIL_FEE,
  BookingDotComFeeType.WOOD_FEE,
  BookingDotComFeeType.WATER_FEE,
  BookingDotComFeeType.TRANSFER_FEE,
  BookingDotComFeeType.LINEN_PACKAGE_FEE,
  BookingDotComFeeType.HEATING_FEE,
  BookingDotComFeeType.AIR_CONDITIONING_FEE,
  BookingDotComFeeType.KITCHEN_LINEN_FEE,
  BookingDotComFeeType.HOUSEKEEPING_FEE,
  BookingDotComFeeType.AIRPORT_SHUTTLE_FEE,
  BookingDotComFeeType.SHUTTLE_BOAT_FEE,
  BookingDotComFeeType.SEA_PLANE_FEE,
  BookingDotComFeeType.SKI_PASS_FEE,
  BookingDotComFeeTypeDeprecated.FINAL_CLEANING_FEE,
  BookingDotComFeeType.WRISTBAND_FEE,
  BookingDotComFeeType.VISA_SUPPORT_FEE,
  BookingDotComFeeType.WATER_PARK_FEE,
  BookingDotComFeeType.CLUB_CARD_FEE,
  BookingDotComFeeType.CONSERVATION_FEE,
  BookingDotComFeeType.CREDIT_CARD_FEE,
  BookingDotComFeeType.GOODS_AND_SERVICES_TAX,
  BookingDotComFeeType.GOVERNMENT_TAX,
  BookingDotComFeeType.SPA_TAX,
  BookingDotComFeeType.HOT_SPRING_TAX,
  BookingDotComFeeType.RESIDENTIAL_TAX,
  BookingDotComFeeType.SAUNA_FITNESS_FACILITIES_TAX,
  BookingDotComFeeType.LOCAL_COUNCIL_TAX,
  BookingDotComFeeType.PETS_FEE,
  BookingDotComFeeType.CITY_TAX,
  BookingDotComFeeType.VAT,
  BookingDotComFeeType.DESTINATION_CHARGE,
  BookingDotComFeeType.DESTINATION_TAX,
  BookingDotComFeeType.GALA_DINNER_FEE,
  BookingDotComFeeType.GOODS_SERVICES_FLOOD_TAX,
  BookingDotComFeeType.GOVERNMENT_CHARGE,
  BookingDotComFeeType.HOT_SPRING_BATH,
  BookingDotComFeeType.INSURANCE_FEE,
  BookingDotComFeeType.INTERNET_FEE,
  BookingDotComFeeType.LOCALITY_CONSERVATION_FEE,
  BookingDotComFeeType.LOUNGE,
  BookingDotComFeeType.PARKING_FEE,
  BookingDotComFeeType.POOL,
  BookingDotComFeeType.SERVICE_CHARGE_ASIA,
  BookingDotComFeeType.SPA,
  BookingDotComFeeType.SUSTAINABILITY_FEE,
  BookingDotComFeeType.TOURISM_FEE,
];

const getAirbnbFeeTypeOptions = ({
  isAmountPercentage,
}: {
  isAmountPercentage: boolean;
}) => {
  return isAmountPercentage
    ? AIRBNB_PERCENT_FEE_TYPES
    : Object.values(AirbnbFeeType);
};

const useFieldsOptions = () => {
  const { t } = useTranslation();
  const { channelSettings } = useFetchAgencyChannelSettings();
  const { isAmountPercentage, isTypeAmount, isTypeFee, scopeValue } =
    useFormData();
  const { defaultValues } = useFormState<PropertyFeeFormSchemaType>();
  const originalBookingMappingType = defaultValues?.bookingMappingType;

  const airbnbMappingType: AirbnbFeeType = useWatch({
    name: 'airbnbMappingType',
  });

  const isScopePetRelated =
    scopeValue === FeeScopeEnum.PER_PET_PER_NIGHT ||
    scopeValue === FeeScopeEnum.PER_PET;

  const isAirbnbMappingTypeWithUnit = [
    AirbnbFeeType.PASS_THROUGH_AIR_CONDITIONING_FEE,
    AirbnbFeeType.PASS_THROUGH_ELECTRICITY_FEE,
    AirbnbFeeType.PASS_THROUGH_HEATING_FEE,
    AirbnbFeeType.PASS_THROUGH_UTILITY_FEE,
    AirbnbFeeType.PASS_THROUGH_WATER_FEE,
  ].includes(airbnbMappingType);

  const isAirbnbActive = channelSettings?.some(
    ({ enumId, isActive }) => enumId === Channel.airbnb && isActive,
  );

  const isAirbnbUnitTypeVisible =
    isAirbnbActive && isAirbnbMappingTypeWithUnit && isTypeFee && isTypeAmount;

  const isAirbnbCleaningFeeCollectionVisible =
    isAirbnbActive &&
    isTypeFee &&
    airbnbMappingType === AirbnbFeeType.PASS_THROUGH_CLEANING_FEE;

  const getAirbnbUnitTypeOptions = () => {
    switch (airbnbMappingType) {
      case AirbnbFeeType.PASS_THROUGH_AIR_CONDITIONING_FEE:
      case AirbnbFeeType.PASS_THROUGH_ELECTRICITY_FEE:
      case AirbnbFeeType.PASS_THROUGH_HEATING_FEE:
      case AirbnbFeeType.PASS_THROUGH_UTILITY_FEE:
      case AirbnbFeeType.PASS_THROUGH_WATER_FEE:
        return [...AIRBNB_UNIT_TYPE_OPTIONS_MAPPING[airbnbMappingType]];

      default:
        return [];
    }
  };

  // eslint-disable-next-line no-nested-ternary
  const AIRBNB_OPTIONS = isScopePetRelated
    ? [AirbnbFeeType.PASS_THROUGH_PET_FEE]
    : isTypeFee
    ? getAirbnbFeeTypeOptions({ isAmountPercentage })
    : Object.values(AirbnbTaxType);
  const AIRBNB_UNIT_TYPE_OPTIONS = getAirbnbUnitTypeOptions();
  const BOOKING_DEPRECATED_OPTIONS = Object.values(
    BookingDotComFeeTypeDeprecated,
  );
  const BOOKING_OPTIONS = orderBy(
    [
      ...Object.values(BookingDotComFeeType),
      ...(BOOKING_DEPRECATED_OPTIONS.includes(
        originalBookingMappingType as BookingDotComFeeTypeDeprecated,
      )
        ? BOOKING_DEPRECATED_OPTIONS
        : []),
    ],
    [{ field: (key) => BOOKING_OPTIONS_ORDER.indexOf(key), order: 'asc' }],
  );
  const HVMB_OPTIONS = Object.values(HVMIFeeType);
  const VRBO_OPTIONS = isScopePetRelated
    ? [VrboFeeType.PET]
    : Object.values(VrboFeeType);

  const CLEANING_FEE_COLLECTION_OPTIONS = Object.values(CleaningFeeCollection);

  const typeOptions = [
    {
      value: FeeTypeEnum.CUSTOM,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.fee',
      ),
    },
    {
      value: FeeTypeEnum.TAX,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.tax',
      ),
    },
  ];

  const conditionTypeOptions = [
    {
      value: FeeConditionEnum.NONE,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.none',
      ),
    },
    {
      value: BY_LENGTH_OF_STAY_SCOPE_OPTION,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.byLengthOfStay',
      ),
    },
    {
      value: FeeConditionEnum.BY_NIGHT,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.byNight',
      ),
    },
  ];

  const conditionByNightOptions = Array.from({ length: 60 }, (_, index) => ({
    value: index + 1,
    label: (index + 1).toString(),
  }));

  const conditionByLengthOfStayOptions = [
    {
      value: FeeConditionEnum.EXEMPT_LONG_STAY,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.exempt',
      ),
    },
    {
      value: FeeConditionEnum.EXCLUSIVE_LONG_STAY,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.onlyApplies',
      ),
    },
  ];

  const commonScopeOptions = [
    {
      value: FeeScopeEnum.PER_STAY,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.scopes.PER_STAY',
      ),
    },
    {
      value: FeeScopeEnum.PER_NIGHT,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.scopes.PER_NIGHT',
      ),
    },
    {
      value: FeeScopeEnum.PER_GUEST,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.scopes.PER_GUEST',
      ),
    },
    {
      value: FeeScopeEnum.PER_GUEST_PER_NIGHT,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.scopes.PER_GUEST_PER_NIGHT',
      ),
    },
  ];

  const amountTypeScopeOptions = [
    ...commonScopeOptions,
    {
      value: FeeScopeEnum.PER_PET,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.scopes.PER_PET',
      ),
    },
    {
      value: FeeScopeEnum.PER_PET_PER_NIGHT,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.scopes.PER_PET_PER_NIGHT',
      ),
    },
  ];

  const percentageScopeOptions = [
    ...commonScopeOptions,
    {
      value: FeeScopeEnum.PERCENTAGE_OF_TOTAL,
      label: t(
        'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.scopes.PERCENTAGE_OF_TOTAL',
      ),
    },
  ];

  const amountScopeOptions = isAirbnbActive
    ? amountTypeScopeOptions
    : amountTypeScopeOptions;

  return {
    AIRBNB_OPTIONS,
    AIRBNB_UNIT_TYPE_OPTIONS,
    BOOKING_OPTIONS,
    CLEANING_FEE_COLLECTION_OPTIONS,
    HVMB_OPTIONS,
    VRBO_OPTIONS,
    typeOptions,
    amountScopeOptions,
    percentageScopeOptions,
    conditionTypeOptions,
    conditionByLengthOfStayOptions,
    isAirbnbCleaningFeeCollectionVisible,
    isAirbnbUnitTypeVisible,
    conditionByNightOptions,
  };
};

export default useFieldsOptions;
